import RAPIER from '@dimforge/rapier3d-compat';
import type { RigidBody, World } from '@dimforge/rapier3d-compat';

import { BALL } from '../game/constants';

/**
 * Rigid-body factories.
 *
 * Everything here creates bodies in WORLD space at rotation identity. In
 * particular the maze body is created at the origin and is never re-created or
 * teleported afterwards - it is driven purely through
 * `setNextKinematicRotation` (see MazeAssembly).
 */

/** A kinematic position-based body: the maze assembly. */
export function createKinematicBody(world: World, x = 0, y = 0, z = 0): RigidBody {
  const desc = RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(x, y, z);
  return world.createRigidBody(desc);
}

/**
 * The ball.
 *
 * CCD plus a contact skin is what stops a small, fast sphere from tunnelling
 * through 45 mm walls.
 *
 * ---------------------------------------------------------------------------
 * WHY THE BALL MUST NEVER SLEEP
 * ---------------------------------------------------------------------------
 * Rapier's sleep test looks at VELOCITY ALONE - it does not ask whether the
 * forces balance. A ball that happens to be momentarily slow while resting on a
 * slope therefore gets put to sleep, and a sleeping body is skipped by the
 * solver *and* by gravity. It stays there permanently.
 *
 * That is not a theoretical concern here. Dropped into a channel a few tens of
 * degrees off the bottom, the ball rolls down, hits the outer ring, and is
 * momentarily near-stationary in the corner; Rapier sleeps it and it never
 * reaches the bottom of its pocket. The whole game depends on the ball rolling
 * downhill, so sleep is disabled outright rather than tuned - there is only ever
 * one dynamic body in this simulation, so the usual reason to allow sleeping
 * (letting a large pile settle) does not apply.
 */
export function createBallBody(world: World, x: number, y: number, z: number): RigidBody {
  const desc = RAPIER.RigidBodyDesc.dynamic()
    .setTranslation(x, y, z)
    .setCcdEnabled(true)
    .setSoftCcdPrediction(BALL.softCcdPrediction)
    .setLinearDamping(BALL.linearDamping)
    .setAngularDamping(BALL.angularDamping)
    .setCanSleep(false);
  return world.createRigidBody(desc);
}
